// Mappers for employee profile GET operations
import type {
  LC_GetEmployeeProfileOut,
  EmployeeProfile,
} from '../types';

// Helper to format backend datetime to yyyy-mm-dd (ISO date for DatePicker component)
const formatBackendDateToISODate = (dateString: string | null): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  // Use UTC methods to avoid timezone issues
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to map employment status text to code
const mapEmploymentStatusTextToCode = (status: string | null): string | null => {
  if (!status) return null;
  const normalized = status.trim().toLowerCase();
  if (normalized === 'vigente' || normalized === 'v') return 'V';
  if (normalized === 'no vigente' || normalized === 'n') return 'N';
  return status;
};

// Helper to convert empty string to null for numeric IDs
const parseNumericId = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'string' ? Number(value) : value;
  return isNaN(num) ? null : num;
};

export const mapEmployeeProfileOutToModel = (
  employee: LC_GetEmployeeProfileOut,
): EmployeeProfile => ({
  id: employee.employeeId,
  userId: employee.userId,
  userName: employee.userName || '',
  rutFull: employee.fullRut || '',
  firstNames: employee.firstNames?.trim() || '',
  lastName: employee.lastName || '',
  secondLastName: employee.secondLastName || '',
  birthDate: formatBackendDateToISODate(employee.birthDate),
  genderCode: employee.genderCode || null,
  maritalStatusId: parseNumericId(employee.maritalStatusCode),
  employmentStatus: mapEmploymentStatusTextToCode(employee.employmentStatus),
  corporateEmail: employee.corporateEmail || '',
  personalEmail: employee.personalEmail || '',
  companyPhoneNumber: employee.companyPhoneNumber || '',
  personalPhoneNumber: employee.personalPhoneNumber || '',
  address: employee.address || '',
  regionId: parseNumericId(employee.regionId),
  communeId: parseNumericId(employee.communeId),
  workAddress: employee.workAddress || '',
  workRegionId: parseNumericId(employee.workRegionId),
  workCommuneId: parseNumericId(employee.workCommuneId),
  doesNotSync: employee.doesNotSync ?? false,
});
